import { apiFetch } from "@/services/api-client";
import type {
  Absence,
  ChatMessage,
  DayStatus,
  DayStatusSetInput,
  GroupCreateInput,
  GroupDetail,
  GroupSummary,
  GroupUpdateInput,
  Membership,
  MembershipStatus,
  PickupArea,
  Plant,
} from "@/types/api";

export async function listGroups(): Promise<GroupSummary[]> {
  return apiFetch<GroupSummary[]>("/groups");
}

export async function getMyGroups(): Promise<GroupDetail[]> {
  return apiFetch<GroupDetail[]>("/groups/mine");
}

export async function getGroup(groupId: string): Promise<GroupDetail> {
  return apiFetch<GroupDetail>(`/groups/${groupId}`);
}

export async function createGroup(input: GroupCreateInput): Promise<GroupDetail> {
  return apiFetch<GroupDetail>("/groups", { method: "POST", body: input });
}

export async function updateGroup(
  groupId: string,
  input: GroupUpdateInput,
): Promise<GroupDetail> {
  return apiFetch<GroupDetail>(`/groups/${groupId}`, { method: "PATCH", body: input });
}

/** The driver (or an admin) ends the crew for good; seats, chat and statuses go with it. */
export async function deleteGroup(groupId: string): Promise<void> {
  await apiFetch<void>(`/groups/${groupId}`, { method: "DELETE" });
}

export async function listPickupAreas(query = ""): Promise<PickupArea[]> {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  return apiFetch<PickupArea[]>(`/pickup-areas${params}`);
}

export async function listPlants(): Promise<Plant[]> {
  return apiFetch<Plant[]>("/plants");
}

export async function requestJoin(
  groupId: string,
  requestMessage: string | null,
  days: number[] | null,
): Promise<Membership> {
  return apiFetch<Membership>(`/groups/${groupId}/join-requests`, {
    method: "POST",
    body: { request_message: requestMessage, days },
  });
}

export async function listJoinRequests(groupId: string): Promise<Membership[]> {
  return apiFetch<Membership[]>(`/groups/${groupId}/join-requests`);
}

export async function decideMembership(
  membershipId: string,
  action: Extract<MembershipStatus, "active" | "declined" | "removed">,
): Promise<Membership> {
  return apiFetch<Membership>(`/memberships/${membershipId}`, {
    method: "PATCH",
    body: { action },
  });
}

export async function leaveGroup(membershipId: string): Promise<Membership> {
  return apiFetch<Membership>(`/memberships/${membershipId}`, { method: "DELETE" });
}

export async function listMessages(groupId: string): Promise<ChatMessage[]> {
  return apiFetch<ChatMessage[]>(`/groups/${groupId}/messages`);
}

export async function postMessage(groupId: string, body: string): Promise<ChatMessage> {
  return apiFetch<ChatMessage>(`/groups/${groupId}/messages`, {
    method: "POST",
    body: { body },
  });
}

export async function addAbsence(membershipId: string, date: string): Promise<Absence> {
  return apiFetch<Absence>(`/memberships/${membershipId}/absences`, {
    method: "POST",
    body: { date },
  });
}

export async function removeAbsence(membershipId: string, absenceId: string): Promise<void> {
  await apiFetch<{ message: string }>(`/memberships/${membershipId}/absences/${absenceId}`, {
    method: "DELETE",
  });
}

export async function setDayStatus(
  groupId: string,
  input: DayStatusSetInput,
): Promise<DayStatus> {
  return apiFetch<DayStatus>(`/groups/${groupId}/day-status`, {
    method: "PUT",
    body: input,
  });
}

/** Shrink my own day set; the freed days can be claimed in another car. */
export async function updateMembershipDays(
  membershipId: string,
  days: number[],
): Promise<Membership> {
  return apiFetch<Membership>(`/memberships/${membershipId}/days`, {
    method: "PATCH",
    body: { days },
  });
}
